import { createStyles } from '@mantine/core';

import { POPULAR_PICK_CARD_WIDTH_PX } from './PopularPickCard.styles';

export const useStyles = createStyles((theme) => ({
  viewAllLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._4,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },

  carouselWrapper: {
    position: 'relative',
  },

  cardsRow: {
    display: 'flex',
    gap: theme.other.spacing._8,
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },

  carouselCard: {
    width: POPULAR_PICK_CARD_WIDTH_PX,
    flexShrink: 0,
  },

  arrowButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: theme.white,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    border: 'none',
    cursor: 'pointer',
    color: theme.colors.gray2[9],
    pointerEvents: 'auto',
    flexShrink: 0,
  },

  fadeLeft: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 80,
    background: `linear-gradient(90deg, ${theme.colors.gray2[0]} 20%, ${theme.colors.gray2[0]}00 100%)`,
    pointerEvents: 'none',
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingLeft: theme.other.spacing._8,
  },

  fadeRight: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 80,
    background: `linear-gradient(270deg, ${theme.colors.gray2[0]} 20%, ${theme.colors.gray2[0]}00 100%)`,
    pointerEvents: 'none',
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: theme.other.spacing._8,
  },

  cardTeamLogo: {
    position: 'absolute',
    bottom: 0,
    left: '-100%',
    opacity: 0.2,
    pointerEvents: 'none',
    aspectRatio: '1 / 1',
  },

  cardGradient: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 40%)',
    pointerEvents: 'none',
    borderRadius: 'inherit',
    border: `2px solid ${theme.white}`,
  },

  cardInnerShadow: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    boxShadow: 'inset 0px 0px 8px 0px white',
  },

  cardUpper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: theme.other.spacing._12,
    width: '100%',
    isolation: 'isolate',
  },

  cardLeagueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    height: 20,
    marginBottom: -theme.other.spacing._12,
    position: 'relative',
    zIndex: 2,
  },

  cardLeague: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    paddingLeft: /* eslint-disable-line local-rules/use-theme-tokens */ 3,
    paddingRight: /* eslint-disable-line local-rules/use-theme-tokens */ 3,
    boxShadow: '0px 0px 24px 0px white',
  },

  cardLeagueIcon: {
    marginBottom: theme.other.spacing._2,
    display: 'flex',
    alignItems: 'center',
  },

  cardLeaguePosition: {
    background: `linear-gradient(180deg, ${theme.colors.gray2[6]} 0%, ${theme.colors.gray2[4]} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textTransform: 'uppercase',
  },

  cardPlayerImage: {
    position: 'relative',
    zIndex: 1,
    width: 64,
    height: 64,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 64,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    border: `2px solid ${theme.white}`,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: -theme.other.spacing._12,
    boxShadow: '0px 8px 40px 0px rgba(0, 0, 0, 0.1)',
  },

  injuryTag: {
    zIndex: 3,
    position: 'absolute',
    '--player-card-tag-left': '50%',
    '--player-card-tag-right': 'auto',
    '--player-card-tag-bottom': '4px',
    '--player-card-tag-transform': 'translate(-50%, 50%)',
    pointerEvents: 'none',
  },

  cardPlayerInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    position: 'relative',
    zIndex: 2,
  },

  cardPlayerName: {
    textAlign: 'center',
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },

  cardMatchup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },

  cardMatchupText: {
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  cardStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 56,
    paddingBottom: theme.other.spacing._8,
    position: 'relative',
    zIndex: 2,
  },

  cardStatValue: {
    letterSpacing: '-0.56px',
    marginBottom: -theme.other.spacing._4,
  },

  cardStatLabel: {
    textAlign: 'center',
    width: '100%',
  },

  cardButtons: {
    display: 'flex',
    gap: theme.other.spacing._8,
    width: '100%',
    position: 'relative',
    zIndex: 2,
  },

  cardButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.other.spacing._2,
    height: 36,
    borderRadius: theme.radius.md,
  },

  skeletonCard: {
    flexShrink: 0,
  },
}));
